import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import FocusTrap from "focus-trap-react";
import { useStringMap } from "../utilities/string-map/useStringMap";
import { CloseIcon } from "../icons/CloseIcon";
import { NakedButton } from "../naked-button/NakedButton";
import { useHideOtherDomElements } from "../hooks/useHideOtherDomElements";
import { useUniqueId } from "../hooks/useUniqueId";
import { useCombineRefs } from "../hooks/useCombineRefs";
import { DialogHeader } from "./DialogHeader";
import { DialogBody } from "./DialogBody";
import { DialogFooter } from "./DialogFooter";
import * as Styled from "./Dialog.styled";
import { stringMapDefault } from "./Dialog.stringMap";
export const DialogInner = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, attachedTo, bodyContent, shouldCloseOnEsc, buttonLayout, children, onKeyDown, onClose, isFocusTrapActive, shouldFocusContainerOnOpen, footerButtons, hasCloseIcon, headerContent, height, icon, setIsFocusTrapActive, focusTrapOptions = {}, isCentered, isConstrainedToPortalRoot, padding, scrimStyle, size, shouldCloseOnOutsideClick, stringMap: stringMapProp, width, onClick, onTouchStart, id, ...rest } = props;
    const stringMap = useStringMap("Dialog", { stringMapDefault, stringMapProp });
    const dialogId = useUniqueId("dialog-", id);
    const [hasClickedOutside, setHasClickedOutside] = useState(false);
    // aria-hide everything except the content (better supported equivalent to setting aria-modal)
    const dialogRef = useRef(null);
    const combinedRef = useCombineRefs(dialogRef, ref);
    useHideOtherDomElements(dialogRef, { isActive: true });
    // DES-3517 - quick fix to allow toasts in already rendered containers to get focused despite being outside of the focus trap
    // This relies on transitionState not switching to entered instantly
    const [containerElements, setContainerElements] = useState([]);
    useEffect(() => {
        setContainerElements([
            dialogRef.current,
            ...document.querySelectorAll(`[data-always-allow-focus="true"]`)
        ]);
    }, []);
    useEffect(() => {
        // FIXME add this to popper's new dialog mode
        const pauseFocusTrapOnOutsideClick = (e) => {
            const isInside = containerElements.some((c) => c?.contains(e.target));
            setHasClickedOutside(Boolean(!isInside));
        };
        window.addEventListener("click", pauseFocusTrapOnOutsideClick, true);
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        // Only lock body scrolling when dialog scrim covers full screen
        if (!isConstrainedToPortalRoot) {
            document.body.style.setProperty("overflow", "hidden");
            scrollbarWidth && document.body.style.setProperty("padding-right", scrollbarWidth + "px");
        }
        return () => {
            window.removeEventListener("click", pauseFocusTrapOnOutsideClick, true);
            if (!isConstrainedToPortalRoot) {
                document.body.style.removeProperty("overflow");
                scrollbarWidth && document.body.style.removeProperty("padding-right");
            }
        };
    }, [onClose, setHasClickedOutside, isConstrainedToPortalRoot, shouldCloseOnOutsideClick, containerElements]);
    const contentWidth = size ? Styled.sizes[size] : Number.isFinite(width) ? `${width}px` : width;
    const contentPadding = padding ? (Number.isFinite(padding) ? `${padding}px` : padding) : undefined;
    const headerRef = useRef(null);
    const footerRef = useRef(null);
    const contentHeight = Number.isFinite(height) ? `${height}px` : height;
    const internalTitleId = headerContent ? `${dialogId}-title` : undefined;
    const internalBodyId = ariaDescribedBy === "" ? undefined : bodyContent ? `${dialogId}-content` : undefined;
    /** Hack to force dialog root motion component to re-render if the ref prop changes.
     * Seems like there'sa bug where it doesn't, caught by our standardComponent tests.
     * Test to see if this is still needed after we upgrade framer-motion to the latest version.
     */
    const refRef = useRef({ key: 0, ref });
    if (refRef.current.ref !== ref) {
        refRef.current.key++;
        refRef.current.ref = ref;
    }
    return (_jsx(FocusTrap, { containerElements: containerElements, active: isFocusTrapActive, paused: hasClickedOutside, focusTrapOptions: {
            escapeDeactivates: false,
            initialFocus: shouldFocusContainerOnOpen ? '[role="dialog"]' : undefined,
            allowOutsideClick: () => true,
            clickOutsideDeactivates: false,
            returnFocusOnDeactivate: true,
            fallbackFocus: `#${dialogId}`,
            // https://github.com/focus-trap/focus-trap-react#testing-in-jsdom
            tabbableOptions: { displayCheck: "none" },
            ...focusTrapOptions
        }, children: _jsx(Styled.DialogRoot, { ref: combinedRef, isConstrainedToPortalRoot: isConstrainedToPortalRoot, scrimStyle: scrimStyle, attachedTo: attachedTo, variants: Styled.backdropMotionVariants, initial: "initial", animate: "animate", exit: "exit", onClick: (e) => {
                onClick?.(e);
                /* Prevent click from bubbling and trigging things like a parent menu close */
                e.stopPropagation();
                e.nativeEvent.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                shouldCloseOnOutsideClick && onClose?.();
            }, onTouchStart: (e) => {
                onTouchStart?.(e);
                /* Prevent click from bubbling and trigging things like a parent menu close */
                e.stopPropagation();
                e.nativeEvent.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                shouldCloseOnOutsideClick && onClose?.();
            }, onKeyDown: (e) => {
                // this should be the only place where Escape is handled, focus-trap shouldn't deactivate
                if (shouldCloseOnEsc && e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose?.();
                }
                onKeyDown?.(e);
            }, ...rest, children: _jsxs(Styled.MotionDialogContents, { "data-testid": "DialogContents", id: dialogId, tabIndex: -1, role: "dialog", onClick: (e) => {
                    onClick?.(e);
                    /* Prevent click from bubbling and trigging things like a parent menu close */
                    e.stopPropagation();
                    e.nativeEvent.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                }, onTouchStart: (e) => {
                    /* Prevent click from bubbling and trigging things like a parent menu close */
                    e.stopPropagation();
                    e.nativeEvent.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                }, "aria-labelledby": ariaLabelledBy || internalTitleId, "aria-describedby": ariaDescribedBy || internalBodyId, "$contentWidth": contentWidth, "$contentPadding": contentPadding, "$contentHeight": contentHeight, "$attachedTo": attachedTo, variants: attachedTo
                    ? attachedTo === "right"
                        ? Styled.slideLeftMotionVariants
                        : Styled.slideRightMotionVariants
                    : Styled.contentMotionVariants, initial: "hidden", animate: "visible", exit: "exit", onAnimationComplete: () => setIsFocusTrapActive(true), children: [icon && _jsx(Styled.DialogIconWrapper, { children: icon }), headerContent && (_jsx(DialogHeader, { id: internalTitleId, isCentered: isCentered, ref: headerRef, children: React.isValidElement(headerContent) ? headerContent : _jsx("h2", { children: headerContent }) })), bodyContent && (_jsx(DialogBody, { id: internalBodyId, isCentered: isCentered, children: bodyContent })), children, footerButtons && (_jsx(DialogFooter, { ref: footerRef, footerButtons: footerButtons, isCentered: isCentered, buttonLayout: buttonLayout })), hasCloseIcon && (_jsx(Styled.DialogCloseIcon, { children: _jsx(NakedButton, { "data-testid": "DialogCloseButton", onClick: onClose, "aria-label": stringMap.ariaLabelCloseDialog, children: _jsx(CloseIcon, { size: "16", "aria-hidden": "true" }) }) }))] }) }, refRef.current.key) }));
});
DialogInner.displayName = "DialogInner";
